if (!customElements.get('pastael-founder-story-slider')) {
  class PastaelFounderStorySlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.track = this.querySelector('[data-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));
      this.root = this.closest('.pastael-founder-story') || this;
      this.dots = Array.from(this.root.querySelectorAll('[data-slider-dot]'));
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');

      if (!this.viewport || !this.track || !this.slides.length) return;

      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.isScrollingProgrammatically = false;
      this.transitionMs = 550;

      this.prevButton?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1));

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      this.slides.forEach((slide, index) => {
        slide.addEventListener('click', (event) => {
          if (index !== this.index) this.goTo(index);
        });
      });

      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.mediaQuery.addEventListener('change', this.handleResize);
      window.addEventListener('resize', this.handleResize);

      this.update(false);
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.handleScroll);
      this.mediaQuery?.removeEventListener('change', this.handleResize);
      window.removeEventListener('resize', this.handleResize);
    }

    get isDesktop() {
      return this.mediaQuery.matches;
    }

    goTo(index) {
      if (!this.slides.length) return;

      const nextIndex = Math.max(0, Math.min(this.slides.length - 1, index));
      this.index = nextIndex;
      this.update(true);
    }

    handleScroll() {
      if (this.isDesktop || this.isScrollingProgrammatically || !this.viewport) return;

      let activeIndex = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      const viewportCenter = this.viewport.scrollLeft + this.viewport.clientWidth / 2;

      this.slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft - this.track.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(slideCenter - viewportCenter);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          activeIndex = index;
        }
      });

      if (activeIndex === this.index) return;

      this.index = activeIndex;
      this.syncControls();
    }

    handleResize() {
      this.update(false);
    }

    syncControls() {
      this.slides.forEach((slide, index) => {
        const offset = index - this.index;
        const isActive = offset === 0;

        slide.classList.toggle('is-active', isActive);
        slide.classList.toggle('is-adjacent', Math.abs(offset) === 1);
        slide.classList.toggle('is-distant', !isActive && Math.abs(offset) !== 1);
        slide.setAttribute('data-offset', offset);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.dots.forEach((dot, index) => {
        const isActive = index === this.index;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.prevButton) {
        const canGoPrev = this.index > 0;
        this.prevButton.classList.toggle('is-visible', canGoPrev);
        this.prevButton.setAttribute('aria-hidden', canGoPrev ? 'false' : 'true');
        this.prevButton.tabIndex = canGoPrev ? 0 : -1;
      }

      if (this.nextButton) {
        const canGoNext = this.index < this.slides.length - 1;
        this.nextButton.classList.toggle('is-visible', canGoNext);
        this.nextButton.setAttribute('aria-hidden', canGoNext ? 'false' : 'true');
        this.nextButton.tabIndex = canGoNext ? 0 : -1;
      }
    }

    update(animate) {
      if (this.isDesktop) {
        if (animate) {
          this.syncControls();
          this.track.style.transition = '';
          this.centerActiveSlide();
          return;
        }

        this.track.style.transition = 'none';
        this.syncControls();
        this.centerActiveSlide();

        requestAnimationFrame(() => {
          this.track.style.transition = '';
        });

        return;
      }

      this.track.style.transform = '';
      this.syncControls();
      this.scrollToActive(animate);
    }

    centerActiveSlide() {
      const slide = this.slides[this.index];
      if (!slide || !this.viewport) return;

      const viewportCenter = this.viewport.clientWidth / 2;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      this.track.style.transform = `translate3d(${viewportCenter - slideCenter}px, 0, 0)`;
    }

    scrollToActive(animate) {
      const slide = this.slides[this.index];
      if (!slide || !this.viewport) return;

      const slideCenter = slide.offsetLeft - this.track.offsetLeft + slide.offsetWidth / 2;
      const scrollLeft = Math.max(0, slideCenter - this.viewport.clientWidth / 2);

      this.isScrollingProgrammatically = true;
      this.viewport.scrollTo({
        left: scrollLeft,
        behavior: animate ? 'smooth' : 'auto',
      });

      window.setTimeout(() => {
        this.isScrollingProgrammatically = false;
      }, this.transitionMs);
    }
  }

  customElements.define('pastael-founder-story-slider', PastaelFounderStorySlider);
}
